import { numberFormat } from "@/utils/number"
import { GetRangesHookParams } from "@/types/zone"
import i18n from "@/i18n"

const getMaximumValue = (
  value: number | string,
  isNegative: boolean,
  step: number,
  max?: number,
): number => {
  if (value === undefined) {
    return isNegative ? step * -1 : max !== undefined ? max : Number.POSITIVE_INFINITY
  }

  const num = Number(value)
  const newMax =
    max !== undefined && (isNegative ? step * -1 : 0) + num > max
      ? max
      : (isNegative ? step * -1 : 0) + num

  return Math.floor(newMax * 10) / 10
}

const getMinimumValue = (
  value: number | string | undefined,
  isNegative: boolean,
  step: number,
): number | undefined => {
  if (value === undefined) {
    return undefined
  }
  const num = Number(value)

  if (!isNegative) {
    if (num === 0) {
      return step
    }

    return Math.floor((num + step) * 10) / 10
  }

  return num
}

const getRanges = ({ max, suffix, func }: GetRangesHookParams) => {
  const generateRange = (isMax: boolean, index: number) => func(isMax, index)

  return Array.from({ length: max }, (_, i) => {
    const isMax = i + 1 === max

    return Object.assign({}, generateRange(isMax, i), {
      label: getLabel(i18n.t(`zone${i + 1}`), suffix),
    })
  })
}

const getHrRanges = ({ max, suffix, func }: GetRangesHookParams) => {
  const generateRange = (isMax: boolean, index: number) => func(isMax, index)

  return Array.from({ length: max }, (_, i) => {
    const isMax = i + 1 === max

    return Object.assign({}, generateRange(isMax, i), {
      label: getLabel(i18n.t(`zone${i}`), suffix),
    })
  })
}

// 번역된 string 을 넘겨야 함 -> 함수 내에서 useTranslation 호출 불가
const getLabel = (zoneString: string, suffix: number | string) => {
  return `${zoneString} (${suffix})`
}

const getValues = (items: Record<string, unknown>[], decimalPoint = 1) => {
  return items.map((item) => numberFormat(item.value as number, decimalPoint))
}

const getCustomRanges = (isMax: boolean, index: number) => {
  return {
    value: isMax ? [index - 1] : [index - 1, index],
    active: isMax ? [true] : [true, true],
    disabled: isMax ? [false] : [false, false],
    readonly: isMax ? [true] : [true, false],
  }
}

const getCustomHrRanges = (isMax: boolean, index: number) => {
  return {
    value: isMax ? [index - 1] : [index - 1, index],
    active: isMax ? [true] : [true, true],
    disabled: isMax ? [false] : [false, false],
    readonly: isMax ? [true] : index - 1 === 0 ? [false, false] : [true, false],
  }
}

const generateZones = (data: Array<string | number>, code: string) =>
  data.map((item, index) => ({
    code: `${code}${code.includes("hr") ? index : index + 1}`,
    value: `${item}`,
  }))

type RangeInputErrorType = "empty" | "notNumeric" | "min" | "max" | null

interface RangeInputValidationResult {
  errorType: RangeInputErrorType
  newMin: number
  newMax: number
}

/**
 * RangeInput 값의 유효성 검사 (에러 타입과 min/max 반환)
 */
const validateRangeInput = (
  inputValue: string | number,
  index: number,
  values: Array<string | number>,
  min: number,
  max: number,
  step: number,
): RangeInputValidationResult => {
  const isNegative = min < 0 && max <= 0

  const newMin =
    (!isNegative
      ? getMinimumValue(values[index - 1], isNegative, step)
      : getMinimumValue(values[index + 1], isNegative, step)) || (!isNegative ? step : min)
  const newMax =
    (!isNegative
      ? getMaximumValue(values[index + 1], isNegative, step, max)
      : getMaximumValue(values[index - 1], isNegative, step, max)) || max

  if (inputValue === "") {
    return { errorType: "empty", newMin, newMax }
  }
  if (typeof inputValue === "string" && isNaN(Number(inputValue))) {
    return { errorType: "notNumeric", newMin, newMax }
  }
  if (Number(inputValue) < newMin) {
    return { errorType: "min", newMin, newMax }
  }
  if (Number(inputValue) > newMax) {
    return { errorType: "max", newMin, newMax }
  }

  return { errorType: null, newMin, newMax }
}

/**
 * RangeInput 값의 에러 여부만 반환
 */
const hasRangeInputError = (
  inputValue: string | number,
  index: number,
  values: Array<string | number>,
  min: number,
  max: number,
  step: number,
): boolean => {
  return validateRangeInput(inputValue, index, values, min, max, step).errorType !== null
}

export {
  getMaximumValue,
  getMinimumValue,
  getRanges,
  getHrRanges,
  getValues,
  getLabel,
  getCustomRanges,
  getCustomHrRanges,
  generateZones,
  validateRangeInput,
  hasRangeInputError,
}

export type { RangeInputErrorType, RangeInputValidationResult }
